import { CacheRequestState, ExtendedRequestState, RequestState } from "../models/RequestState";
import InstanceClient from "../services/InstanceClient";
import StatusCode from "../util/StatusCode";
import { Dispatch } from "react"
import { Action, Reducer } from "redux"
import { CoreModuleInstanceLogEntry, PageResult, CoreModuleInstanceListing, CoreModuleInstance } from "../models/CoreModels";
import { InstanceLogSearchPageRequest, PageRequest } from "../models/Requests";


enum ModuleInstanceType {
    GET_INSTANCE = 'GET_INSTANCE',
    GET_INSTANCES = 'GET_INSTANCES',
    GET_INSTANCE_LOGS = 'GET_INSTANCE_LOGS',
    GET_INSTANCE_LOGS_UPDATE = 'GET_INSTANCE_LOGS_UPDATE',
    GET_INSTANCE_LOGS_EVENTIDS = 'GET_INSTANCE_LOGS_EVENTIDS',
};

export type InstanceRequestState = ExtendedRequestState<PageResult<CoreModuleInstanceListing>, { filter: Partial<PageRequest> }>

export interface ModuleInstanceState {
    moduleInstance: RequestState<CoreModuleInstance>
    instances: InstanceRequestState
    instanceLogs: CacheRequestState<PageResult<CoreModuleInstanceLogEntry>>
    eventIds: RequestState<number[]>
};

interface ModuleInstance_Get extends Action<ModuleInstanceType> {
    type: ModuleInstanceType.GET_INSTANCE,
    moduleInstance: RequestState<CoreModuleInstance>
};

interface ModuleInstance_GetInstances extends Action<ModuleInstanceType> {
    type: ModuleInstanceType.GET_INSTANCES,
    instances: InstanceRequestState
}

interface ModuleInstance_InstanceLogs extends Action<ModuleInstanceType> {
    type: ModuleInstanceType.GET_INSTANCE_LOGS,
    instanceLogs: CacheRequestState<PageResult<CoreModuleInstanceLogEntry>>
}

interface ModuleInstance_InstanceLogs_Update extends Action<ModuleInstanceType> {
    type: ModuleInstanceType.GET_INSTANCE_LOGS_UPDATE
}

interface ModuleInstance_InstanceLogs_EventIds extends Action<ModuleInstanceType> {
    type: ModuleInstanceType.GET_INSTANCE_LOGS_EVENTIDS,
    eventIds: RequestState<number[]>
}

type ModuleInstanceAction =
    ModuleInstance_Get
    | ModuleInstance_GetInstances
    | ModuleInstance_InstanceLogs
    | ModuleInstance_InstanceLogs_Update
    | ModuleInstance_InstanceLogs_EventIds;


export const getModuleInstance = (moduleId: string, moduleInstanceId: string) => (dispatch: Dispatch<ModuleInstance_Get>) => {
    dispatch({
        type: ModuleInstanceType.GET_INSTANCE,
        moduleInstance: { code: StatusCode.PENDING }
    });
    return InstanceClient.get<CoreModuleInstance>(`/api/core/module/${moduleId}/instance/${moduleInstanceId}`)
        .then(res => {
            dispatch({
                type: ModuleInstanceType.GET_INSTANCE,
                moduleInstance: { code: StatusCode.COMPLETE, data: res.data }
            });
        })
        .catch(error => {
            dispatch({
                type: ModuleInstanceType.GET_INSTANCE,
                moduleInstance: { code: StatusCode.ERROR, error }
            });
        })
}

export const getInstances = (moduleId: string, filter: Partial<PageRequest> = {}) => (dispatch: Dispatch<ModuleInstance_GetInstances>) => {
    dispatch({
        type: ModuleInstanceType.GET_INSTANCES,
        instances: { code: StatusCode.PENDING, filter }
    });
    return InstanceClient.get<PageResult<CoreModuleInstanceListing>>(`/api/core/module/${moduleId}/instances`, filter)
        .then(response => {
            dispatch({
                type: ModuleInstanceType.GET_INSTANCES,
                instances: {
                    code: StatusCode.COMPLETE,
                    data: response.data,
                    filter: filter
                }
            });
        })
        .catch(error => {
            return dispatch({
                type: ModuleInstanceType.GET_INSTANCES,
                instances: { code: StatusCode.ERROR, error, filter }
            })
        });
}

export const getInstanceLogs = (moduleId: string, instanceId: string, filter: Partial<InstanceLogSearchPageRequest> = {}, update: boolean = false) => (dispatch: Dispatch<ModuleInstance_InstanceLogs | ModuleInstance_InstanceLogs_Update>) => {
    if (update) {
        dispatch({ type: ModuleInstanceType.GET_INSTANCE_LOGS_UPDATE });
    } else {
        dispatch({
            type: ModuleInstanceType.GET_INSTANCE_LOGS,
            instanceLogs: { code: StatusCode.PENDING }
        });
    }
    return InstanceClient.get<PageResult<CoreModuleInstanceLogEntry>>(`/api/core/module/${moduleId}/instances/${instanceId}/log`, filter)
        .then(res => {
            dispatch({
                type: ModuleInstanceType.GET_INSTANCE_LOGS,
                instanceLogs: {
                    code: StatusCode.COMPLETE,
                    data: res.data,
                }
            });
        })
        .catch(error => {
            return dispatch({
                type: ModuleInstanceType.GET_INSTANCE_LOGS,
                instanceLogs: { code: StatusCode.ERROR, error: error }
            });
        });
}

export const getEventIds = (moduleId: string, instanceId: string) => (dispatch: Dispatch<ModuleInstance_InstanceLogs_EventIds>) => {
    dispatch({
        type: ModuleInstanceType.GET_INSTANCE_LOGS_EVENTIDS,
        eventIds: { code: StatusCode.PENDING }
    });
    return InstanceClient.get<number[]>(`/api/core/module/${moduleId}/instances/${instanceId}/events`)
        .then(res => {
            dispatch({
                type: ModuleInstanceType.GET_INSTANCE_LOGS_EVENTIDS,
                eventIds: { code: StatusCode.COMPLETE, data: res.data }
            });
        })
        .catch(error => {
            dispatch({
                type: ModuleInstanceType.GET_INSTANCE_LOGS_EVENTIDS,
                eventIds: { code: StatusCode.ERROR, error }
            });
        });
};

export const downloadInstanceLogs = (moduleId: string, instanceId: string, filter: Partial<InstanceLogSearchPageRequest> = {}) => {
    return InstanceClient.get<string>(`/api/core/module/${moduleId}/instances/${instanceId}/log/download`, filter)
        .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            let fileName = `InstanceLog-${instanceId}.csv`;

            const contentDisposition = res.headers['content-disposition'];
            if (contentDisposition) {
                var matches = /filename=([^;]*);/.exec(contentDisposition);
                if (matches !== null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        });
}


const initialState: ModuleInstanceState = {
    moduleInstance: { code: StatusCode.NONE },
    instances: { code: StatusCode.NONE, filter: {} },
    instanceLogs: { code: StatusCode.NONE },
    eventIds: { code: StatusCode.NONE },
};

export const reducer: Reducer<ModuleInstanceState, ModuleInstanceAction> = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case ModuleInstanceType.GET_INSTANCE:
            return {
                ...state,
                moduleInstance: action.moduleInstance
            };
        case ModuleInstanceType.GET_INSTANCES:
            return {
                ...state,
                instances: action.instances
            };
        case ModuleInstanceType.GET_INSTANCE_LOGS:
            return {
                ...state,
                instanceLogs: action.instanceLogs,
            };
        case ModuleInstanceType.GET_INSTANCE_LOGS_UPDATE:
            return {
                ...state,
                instanceLogs: { ...state.instanceLogs, code: StatusCode.PENDING }
            };
        case ModuleInstanceType.GET_INSTANCE_LOGS_EVENTIDS:
            return {
                ...state,
                eventIds: action.eventIds 
            };
        default:
            return state;
    }
}